import React, { Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { reduce } from 'lodash';
import { Helmet } from 'react-helmet';
import { Sidebar } from '@src/components/Sidebar';
import { SkillsReview } from '@src/components/SkillsReview';
import { SalaryReview } from '@src/components/SalaryReview';
import Checkbox from '@src/components/Checkbox';
import { Container } from '@src/components/Container';
import { Appearance, Button } from '@src/components/Button';
import { FUNNEL_PITCH_ROUTE } from '@src/Routes/links';
import { DEFAULT_CURRENCY_SYMBOL, OPENING_PAID_ADDONS, OPENING_PAID_ADDONS_BY_TYPE } from '@src/constants';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackEvent, trackViewCreatePost } from '@src/metrics';
import { idify } from '@src/util/idify';
import { OPENING_ROUTE } from '@src/components/Preview/appRoutes';
import { ActiveTab } from '@src/components/Sidebar/enums/activeTab.enum';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { OpeningPaidAddonEnum, OpeningVisibilityEnum } from '@src/graphql/generated';
import { OpeningServices } from './OpeningServices';
import './styles.scss';

const View = ({
  opening,
  companyProfile,
  publishing,
  publish,
  togglePricing,
  checkSubscriptionState,
  header: Header,
  changeField,
  checkOpeningLimit,
  benefits,
}) => {
  const openingId = idify(opening);
  const activeOpeningPaidAddons = OPENING_PAID_ADDONS.filter(({ isActive }) => isActive);

  function toggleAllowDistribution(value) {
    changeField('allowDistribution', !value);
    trackEvent({
      context: MetricContext.CREATE_OPENING,
      interaction: MetricAction.TOGGLE_SOCIAL_SHARING_PREFERENCE,
      payload: {
        'Opening Id': openingId,
        'Social Sharing Enabled': value,
      },
    });
  }

  function setSelectedAddons(selectedAddon) {
    if (selectedAddon.type === OPENING_PAID_ADDONS_BY_TYPE[OpeningPaidAddonEnum.PUBLIC_OPENING]?.type) {
      changeField(
        'visibility',
        opening.visibility === OpeningVisibilityEnum.PUBLIC
          ? OpeningVisibilityEnum.UNLISTED
          : OpeningVisibilityEnum.PUBLIC,
      );

      return;
    }

    const isSelected = opening.paidAddons.some(addon => addon.type === selectedAddon.type);

    if (isSelected) {
      changeField(
        'openingPaidAddons',
        opening.paidAddons.filter(addon => addon.type !== selectedAddon.type),
      );
    } else {
      changeField('openingPaidAddons', [...opening.paidAddons, selectedAddon]);
    }
  }

  // check if user has not reached active opening limit
  function handleOpeningLimitCheck() {
    checkOpeningLimit({
      nextFunction: publish, // publish opening
    });
  }

  return (
    <Fragment>
      <div className="funnel-review-content page">
        <Helmet>
          <title>Opening review</title>
        </Helmet>

        {Header && <Header opening={opening} togglePricing={togglePricing} />}

        <div className={classnames('page-layout', { incomplete: !companyProfile.isComplete })}>
          {companyProfile.isComplete && (
            <Sidebar
              benefits={benefits}
              companyProfile={companyProfile}
              defaultTab={ActiveTab.PREVIEW}
              isTabsDisabled
              opening={opening}
              previewUrl={OPENING_ROUTE}
            />
          )}

          <Container className="page-content" type={ContainerType.BORDERED}>
            <h4 className="page-content__title">{opening.title}</h4>

            <SkillsReview opening={opening} />

            <SalaryReview opening={opening} />

            {activeOpeningPaidAddons.length > 0 ? (
              <OpeningServices
                opening={opening}
                paidAddons={activeOpeningPaidAddons}
                setSelectedAddons={setSelectedAddons}
              />
            ) : null}

            {publishing ? (
              <div className="funnel-review-content-footer sending">Publishing...</div>
            ) : (
              <div className="funnel-review-content-footer">
                <div className="funnel-review-content-footer-actions">
                  <div className="funnel-review-content-section with-border funnel-review-content-publish">
                    <Checkbox
                      label="I do not want my job opening to be promoted on social media and to get free extra reach"
                      onChange={toggleAllowDistribution}
                      value={!opening.allowDistribution}
                      wrapperClassName="funnel-review-content-ad-distribution"
                    />

                    <div className="funnel-review-content-actions">
                      <Button
                        isDisabled={publishing}
                        onClick={() => {
                          checkSubscriptionState(
                            {
                              eventSource: MetricEventSource.PUBLISH_BUTTON,

                              isNetworkOnly: true,

                              metricsPrevContext: MetricContext.CREATE_OPENING_REVIEW,
                              // check if user has active subscription
                              nextFunction: handleOpeningLimitCheck,
                              onCompleted: handleOpeningLimitCheck,
                            },
                            true,
                          );

                          trackEvent({
                            context: MetricContext.CREATE_OPENING,
                            interaction: MetricAction.PUBLISH_OPENING,
                            payload: { 'Opening Id': openingId },
                          });
                        }}
                      >
                        Publish
                      </Button>

                      <Button
                        appearance={Appearance.INVISIBLE}
                        className="funnel-review-content__back-button"
                        isLink
                        onClick={() =>
                          trackViewCreatePost({
                            'Event Source': MetricEventSource.BACK_BUTTON,
                            'Opening Id': openingId,
                            'Previous Context': MetricContext.CREATE_OPENING_REVIEW,
                          })
                        }
                        params={{ openingId }}
                        to={FUNNEL_PITCH_ROUTE}
                      >
                        Back
                      </Button>

                      <div className="funnel-review-content-actions__total-cost">
                        Total premium services cost:{' '}
                        <span>
                          {DEFAULT_CURRENCY_SYMBOL}
                          {reduce(
                            opening.paidAddons,
                            (result, item) => {
                              const addon = OPENING_PAID_ADDONS.find(addon => addon.type === item.type);

                              const addonActivePrice = addon?.discountedPriceInEur ?? addon?.priceInEur ?? 0;

                              return result + addonActivePrice;
                            },
                            0,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Container>
        </div>
      </div>
    </Fragment>
  );
};

View.propTypes = {
  benefits: PropTypes.arrayOf(PropTypes.object.isRequired),
  changeField: PropTypes.func.isRequired,
  checkOpeningLimit: PropTypes.func.isRequired,
  checkSubscriptionState: PropTypes.func.isRequired,
  companyProfile: PropTypes.object.isRequired,
  header: PropTypes.func,
  opening: PropTypes.object.isRequired,
  publish: PropTypes.func.isRequired,
  publishing: PropTypes.bool.isRequired,
  togglePricing: PropTypes.func,
};

export default View;
